/**
 * New Post Watching (#68-#76, #94) — the settings tab, the row toggles, the `/watch` feed, the
 * global snooze and the dedicated watch Telegram bot.
 *
 * Watching is a standalone subsystem sharing the identity layer with monitoring, so it gets its own
 * resource module rather than riding on `settings.ts`: the `watch` blob is written by #69 alone
 * (never by #21), and the watch bot is a second binding behind #73-#76 with the same three-face
 * contract the viral bot uses — both answer with the full state payload so the tab replaces its
 * cache instead of refetching.
 */

import type {
  SubjectType,
  TelegramStateResponse,
  WatchEstimateBody,
  WatchEstimateResponse,
  WatchFeedQuery,
  WatchFeedResponse,
  WatchSettingsUpdate,
  WatchSnoozeBody,
  WatchStateResponse,
  WatchTargetBody,
  WatchTargetResponse,
} from '@lw/shared';
import { apiJson, apiVoid } from './client';
import { viewFilterQuery } from './views';

/* -------------------------------------------------------------- settings tab */

/** #68 `GET /api/watch` — settings blob, stats, paused reasons and the bot's bound flag. */
export function getWatch(signal?: AbortSignal): Promise<WatchStateResponse> {
  return apiJson<WatchStateResponse>('/watch', { ...(signal ? { signal } : {}) });
}

/** #69 `PUT /api/watch/settings` — a partial blob (one tab section at a time); answers with #68. */
export function putWatchSettings(body: WatchSettingsUpdate): Promise<WatchStateResponse> {
  return apiJson<WatchStateResponse>('/watch/settings', { method: 'PUT', body });
}

/**
 * #94 `POST /api/watch/snooze` — the global pause, ahead of the master toggle. `preset: null`
 * resumes; the answer is the whole #68 payload, so the caller replaces its cache.
 */
export function postWatchSnooze(body: WatchSnoozeBody): Promise<WatchStateResponse> {
  return apiJson<WatchStateResponse>('/watch/snooze', { method: 'POST', body });
}

/* ------------------------------------------------------------------ targets */

/**
 * #70 `PUT /api/watch/targets/:t/:id` — the row toggle and the group override dialog.
 * `cadenceMinutes`/`smart` are legal on groups only; `null` clears an override back to inherit.
 */
export function putWatchTarget(
  t: SubjectType,
  id: number,
  body: WatchTargetBody,
): Promise<WatchTargetResponse> {
  return apiJson<WatchTargetResponse>(`/watch/targets/${t}/${id}`, { method: 'PUT', body });
}

/* --------------------------------------------------------------------- feed */

/**
 * #71 `GET /api/watch/feed` — the global feed's serializer against the watch scope.
 *
 * Identical wire shape to `getGlobalFeed`, because #71 *is* #51 narrowed to watch-caught posts;
 * `viewFilterQuery` already forwards `q`, which is the one param this screen sends and `/feed`
 * does not.
 */
export function getWatchFeed(
  params: Partial<WatchFeedQuery> = {},
  signal?: AbortSignal,
): Promise<WatchFeedResponse> {
  return apiJson<WatchFeedResponse>('/watch/feed', {
    query: {
      ...viewFilterQuery(params),
      authorIds: undefined,
      groupIds: params.groupIds,
      profileIds: params.profileIds,
      sort: params.sort,
      dir: params.dir,
      cursor: params.cursor,
      limit: params.limit,
    },
    signal,
  });
}

/* ----------------------------------------------------------------- estimate */

/** #72 `POST /api/watch/estimate` — the live cost card; the whole draft goes over, debounced. */
export function postWatchEstimate(
  body: WatchEstimateBody,
  signal?: AbortSignal,
): Promise<WatchEstimateResponse> {
  return apiJson<WatchEstimateResponse>('/watch/estimate', { method: 'POST', body, signal });
}

/* ----------------------------------------------------------------- telegram */

/** #73 — #52's shape against `settings.telegramWatch`. */
export function getWatchTelegram(signal?: AbortSignal): Promise<TelegramStateResponse> {
  return apiJson<TelegramStateResponse>('/watch/telegram', { ...(signal ? { signal } : {}) });
}

/** #74 — handshake; #53 semantics. */
export function connectWatchTelegram(token: string): Promise<TelegramStateResponse> {
  return apiJson<TelegramStateResponse>('/watch/telegram/connect', {
    method: 'POST',
    body: { token },
  });
}

/** #75 — unbind; #54 semantics (queued `telegram_watch` deliveries are skipped). */
export function disconnectWatchTelegram(): Promise<void> {
  return apiVoid('/watch/telegram/disconnect', { method: 'POST' });
}

/** #76 — test message; #55 semantics. */
export function testWatchTelegram(): Promise<TelegramStateResponse> {
  return apiJson<TelegramStateResponse>('/watch/telegram/test', { method: 'POST' });
}
